import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ExclamationCircleIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { financeiroApi } from '../../../services/api';
import type { CategoriaFinanceira, PaginatedResponse } from '../../../types';

interface CategoriaComSubcategorias extends CategoriaFinanceira {
  subcategorias?: CategoriaFinanceira[];
  isExpanded?: boolean;
}

const CategoriasList: React.FC = () => {
  const navigate = useNavigate();
  const [categorias, setCategorias] = useState<CategoriaComSubcategorias[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Search and Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    tipo: '',
    ativa: '',
  });

  // Pagination (for parent categories only)
  const [pagination, setPagination] = useState({
    count: 0,
    next: null as string | null,
    previous: null as string | null,
    current_page: 1
  });
  const [pageSize, setPageSize] = useState(10);

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setSearchTerm(searchInput);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadCategorias(1, pageSize);
  }, [searchTerm, filters, pageSize]);

  const loadCategorias = async (page = 1, size = pageSize) => {
    try {
      setLoading(true);

      // Load all categories (we'll filter and paginate in frontend)
      const params: {
        page_size?: number;
        search?: string;
        tipo?: string;
        ativa?: boolean;
        ordering?: string;
      } = {
        page_size: 1000, // Load all
        search: searchTerm,
        ordering: 'tipo,codigo'
      };

      if (filters.tipo) {
        params.tipo = filters.tipo;
      }
      if (filters.ativa) {
        params.ativa = filters.ativa === 'true';
      }

      const response: PaginatedResponse<CategoriaFinanceira> = await financeiroApi.getCategorias(params);

      // As subcategorias já vêm no campo 'subcategorias' do serializer
      const allCategorias = response.results;
      const parentCategories = allCategorias.filter(cat => !cat.categoria_pai);

      // Build hierarchy with subcategorias from API
      const categoriasComSubs: CategoriaComSubcategorias[] = parentCategories.map(parent => ({
        ...parent,
        subcategorias: parent.subcategorias || [],
        isExpanded: expandedCategories.has(parent.id)
      }));

      // Apply pagination to parent categories only
      const totalParents = categoriasComSubs.length;
      const startIdx = (page - 1) * size;
      const endIdx = startIdx + size;
      const paginatedParents = categoriasComSubs.slice(startIdx, endIdx);

      setCategorias(paginatedParents);
      setPagination({
        count: totalParents,
        next: endIdx < totalParents ? 'next' : null,
        previous: startIdx > 0 ? 'prev' : null,
        current_page: page
      });
      setError(null);
    } catch (err) {
      setError('Erro ao carregar categorias');
      console.error('Error loading categorias:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (categoriaId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoriaId)) {
        newSet.delete(categoriaId);
      } else {
        newSet.add(categoriaId);
      }
      return newSet;
    });

    // Update the local state
    setCategorias(prev => prev.map(cat =>
      cat.id === categoriaId ? { ...cat, isExpanded: !cat.isExpanded } : cat
    ));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setFilters({
      tipo: '',
      ativa: '',
    });
  };

  const handleDelete = async (id: string, nome: string, isParent: boolean) => {
    const confirmMessage = isParent
      ? `Tem certeza que deseja excluir a categoria "${nome}" e todas suas subcategorias?`
      : `Tem certeza que deseja excluir a subcategoria "${nome}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await financeiroApi.deleteCategoria(id);
      setSuccessMessage('Categoria excluída com sucesso!');
      setTimeout(() => setSuccessMessage(null), 3000);
      loadCategorias(pagination.current_page, pageSize);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao excluir categoria');
      setTimeout(() => setError(null), 5000);
    }
  };

  const getTipoLabel = (tipo: string) => {
    const tipos: Record<string, string> = {
      'R': 'Receita',
      'D': 'Despesa',
      'T': 'Transferência'
    };
    return tipos[tipo] || tipo;
  };

  const getTipoBadgeClass = (tipo: string) => {
    const classes: Record<string, string> = {
      'R': 'bg-green-100 text-green-800',
      'D': 'bg-red-100 text-red-800',
      'T': 'bg-blue-100 text-blue-800'
    };
    return classes[tipo] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-secondary-900">Categorias Financeiras</h1>
          <p className="text-xs text-secondary-600">
            Gerenciar categorias e subcategorias de receitas, despesas e transferências
          </p>
        </div>
        <button
          onClick={() => navigate('/financeiro/categorias/new')}
          className="btn-primary whitespace-nowrap"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Nova Categoria
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
              <input
                type="text"
                placeholder="Buscar categorias (mín. 3 caracteres)..."
                className="input-field pl-10"
                value={searchInput}
                onChange={handleSearch}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4">
            <select
              className="input-field"
              value={filters.tipo}
              onChange={(e) => handleFilterChange('tipo', e.target.value)}
            >
              <option value="">Todos os tipos</option>
              <option value="R">Receita</option>
              <option value="D">Despesa</option>
              <option value="T">Transferência</option>
            </select>

            <select
              className="input-field"
              value={filters.ativa}
              onChange={(e) => handleFilterChange('ativa', e.target.value)}
            >
              <option value="">Todos os status</option>
              <option value="true">Ativa</option>
              <option value="false">Inativa</option>
            </select>

            <button
              onClick={clearFilters}
              className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-500 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
            >
              Limpar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-secondary-600">Carregando categorias...</p>
          </div>
        ) : categorias.length === 0 ? (
          <div className="text-center py-8">
            <ExclamationCircleIcon className="h-12 w-12 text-secondary-400 mx-auto" />
            <p className="mt-2 text-secondary-600">Nenhuma categoria encontrada</p>
            <p className="mt-1 text-xs text-secondary-500">
              Tente ajustar os filtros ou cadastre uma nova categoria.
            </p>
          </div>
        ) : (
          <>
            {/* Responsive Cards for Mobile */}
            <div className="block lg:hidden space-y-3">
              {categorias.map((categoria) => (
                <div key={categoria.id}>
                  {/* Parent Category Card */}
                  <div className="border border-secondary-200 rounded-lg p-3 bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {categoria.subcategorias && categoria.subcategorias.length > 0 && (
                            <button
                              onClick={() => toggleExpand(categoria.id)}
                              className="text-secondary-600 hover:text-secondary-900"
                            >
                              {categoria.isExpanded ? (
                                <ChevronDownIcon className="h-4 w-4" />
                              ) : (
                                <ChevronRightIcon className="h-4 w-4" />
                              )}
                            </button>
                          )}
                          <h3 className="text-sm font-medium text-secondary-900">{categoria.nome}</h3>
                        </div>
                        <p className="text-xs text-secondary-500">Código: {categoria.codigo}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs text-secondary-600 mb-2">
                      <div>
                        <span className="font-medium">Tipo:</span>{' '}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTipoBadgeClass(categoria.tipo)}`}>
                          {getTipoLabel(categoria.tipo)}
                        </span>
                      </div>
                      {categoria.subcategorias && categoria.subcategorias.length > 0 && (
                        <div>
                          <span className="font-medium">Subcategorias:</span> {categoria.subcategorias.length}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/financeiro/categorias/${categoria.id}/edit`)}
                        className="text-yellow-600 hover:text-yellow-900 p-1"
                        title="Editar"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(categoria.id, categoria.nome, true)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Excluir"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Subcategories (when expanded) */}
                  {categoria.isExpanded && categoria.subcategorias && categoria.subcategorias.length > 0 && (
                    <div className="ml-6 mt-2 space-y-2">
                      {categoria.subcategorias.map((sub) => (
                        <div key={sub.id} className="border border-secondary-200 rounded-lg p-2 bg-secondary-25">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="text-xs font-medium text-secondary-900">↳ {sub.nome}</h4>
                              <p className="text-xs text-secondary-500">Código: {sub.codigo}</p>
                            </div>
                            <div className="flex space-x-1">
                              <button
                                onClick={() => navigate(`/financeiro/categorias/${sub.id}/edit`)}
                                className="text-yellow-600 hover:text-yellow-900 p-1"
                                title="Editar"
                              >
                                <PencilIcon className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleDelete(sub.id, sub.nome, false)}
                                className="text-red-600 hover:text-red-900 p-1"
                                title="Excluir"
                              >
                                <TrashIcon className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Table for Desktop */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider w-32">Código</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Categoria</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Subcategorias</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider w-32">Tipo</th>
                    <th className="px-2 py-1.5 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider w-24">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {categorias.map((categoria) => (
                    <React.Fragment key={categoria.id}>
                      {/* Parent Category Row */}
                      <tr className="hover:bg-secondary-25">
                        <td className="px-2 py-1.5 text-xs text-secondary-900">{categoria.codigo}</td>
                        <td className="px-2 py-1.5 text-xs font-medium text-secondary-900">{categoria.nome}</td>
                        <td className="px-2 py-1.5 text-xs">
                          {categoria.subcategorias && categoria.subcategorias.length > 0 ? (
                            <button
                              onClick={() => toggleExpand(categoria.id)}
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                            >
                              {categoria.isExpanded ? (
                                <>
                                  <ChevronDownIcon className="h-4 w-4" />
                                  <span>{categoria.subcategorias.length} subcategorias</span>
                                </>
                              ) : (
                                <>
                                  <ChevronRightIcon className="h-4 w-4" />
                                  <span>{categoria.subcategorias.length} subcategorias</span>
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="text-secondary-400">-</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTipoBadgeClass(categoria.tipo)}`}>
                            {getTipoLabel(categoria.tipo)}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <div className="flex justify-end space-x-1">
                            <button
                              onClick={() => navigate(`/financeiro/categorias/${categoria.id}/edit`)}
                              className="text-yellow-600 hover:text-yellow-900 p-0.5"
                              title="Editar"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(categoria.id, categoria.nome, true)}
                              className="text-red-600 hover:text-red-900 p-0.5"
                              title="Excluir"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Subcategory Rows (when expanded) */}
                      {categoria.isExpanded && categoria.subcategorias && categoria.subcategorias.map((sub) => (
                        <tr key={sub.id} className="bg-secondary-25 hover:bg-secondary-50">
                          <td className="px-2 py-1.5 text-xs text-secondary-700 pl-8">{sub.codigo}</td>
                          <td className="px-2 py-1.5 text-xs text-secondary-700 pl-8">↳ {sub.nome}</td>
                          <td className="px-2 py-1.5 text-xs text-secondary-400">-</td>
                          <td className="px-2 py-1.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTipoBadgeClass(sub.tipo)}`}>
                              {getTipoLabel(sub.tipo)}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <div className="flex justify-end space-x-1">
                              <button
                                onClick={() => navigate(`/financeiro/categorias/${sub.id}/edit`)}
                                className="text-yellow-600 hover:text-yellow-900 p-0.5"
                                title="Editar"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => handleDelete(sub.id, sub.nome, false)}
                                className="text-red-600 hover:text-red-900 p-0.5"
                                title="Excluir"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.count > 0 && (
              <div className="flex items-center justify-between border-t border-secondary-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => loadCategorias(pagination.current_page - 1, pageSize)}
                    disabled={!pagination.previous}
                    className="relative inline-flex items-center rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => loadCategorias(pagination.current_page + 1, pageSize)}
                    disabled={!pagination.next}
                    className="relative ml-3 inline-flex items-center rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div className="flex items-center space-x-4">
                    <p className="text-xs text-secondary-700">
                      Mostrando{' '}
                      <span className="font-medium">
                        {(pagination.current_page - 1) * pageSize + 1}
                      </span>{' '}
                      até{' '}
                      <span className="font-medium">
                        {Math.min(pagination.current_page * pageSize, pagination.count)}
                      </span>{' '}
                      de{' '}
                      <span className="font-medium">{pagination.count}</span>{' '}
                      categorias principais
                    </p>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-secondary-700">Por página:</label>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value);
                          setPageSize(newSize);
                          loadCategorias(1, newSize);
                        }}
                        className="text-sm border border-secondary-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => loadCategorias(pagination.current_page - 1, pageSize)}
                        disabled={!pagination.previous}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Anterior</span>
                        ←
                      </button>
                      <button
                        onClick={() => loadCategorias(pagination.current_page + 1, pageSize)}
                        disabled={!pagination.next}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Próximo</span>
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoriasList;
